import { CommonModule } from '@angular/common';
import {
  type AfterViewInit,
  Component,
  computed,
  type ElementRef,
  inject,
  input,
  type InputSignal,
  type OnDestroy,
  type Signal,
  signal,
  viewChild,
  type WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import { hasServiceSync } from '@auth/repository/auth.repository';
import {
  type INavigationItem,
  type ItemInfo,
  MENU_LABEL,
} from '@core/models/navigation-item.model';
import { UME_SERVICE } from '@core/services/ume-services';
import { isAllSubItemsRequireUpgrade, isUpgradeRequired } from '@helpers/is-nav-item-need-upgrade';
import { WrapFnPipe } from '@shared/pipes/wrapFn.pipe';

import { CompanyTypeBasedRoutePipe } from '../pipes/company-type-based-route.pipe';
import { CustomEventService } from '../repository/customEvent.service';
import { MainNavMenuPanelComponent } from './main-nav-menu-panel/main-nav-menu-panel.component';
import { handleNavigationClick } from './nav-helpers';
import { UmeLockIconComponent } from './ume-lock-icon/ume-lock-icon.component';

@Component({
  selector: 'ume-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  imports: [
    CommonModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    RouterLink,
    RouterLinkActive,
    CompanyTypeBasedRoutePipe,
    MainNavMenuPanelComponent,
    WrapFnPipe,
    UmeLockIconComponent,
  ],
})
export class NavigationComponent implements AfterViewInit, OnDestroy {
  protected readonly isUpgradeRequired = isUpgradeRequired;
  protected readonly isAllSubItemsRequireUpgrade = isAllSubItemsRequireUpgrade;

  private readonly router: Router = inject(Router);
  private readonly customEventService: CustomEventService = inject(CustomEventService);

  public items: InputSignal<INavigationItem[]> = input.required();

  private _resizeObserver!: ResizeObserver;
  private readonly _containerWidth: WritableSignal<number> = signal(0);
  private readonly _itemsTotalWidth: WritableSignal<number> = signal(0);

  public readonly premiumLabel = MENU_LABEL.PREMIUM;
  public readonly isProvider = hasServiceSync(UME_SERVICE.PROVIDER);
  public readonly showMatMenu: Signal<boolean> = computed(() => {
    // Add a small buffer to avoid flickering due to minor pixel differences
    const buffer = 8;
    return this._containerWidth() < this._itemsTotalWidth() + buffer;
  });

  menuContainer: Signal<ElementRef<HTMLDivElement>> = viewChild.required('menuContainer');
  navULMenu: Signal<ElementRef<HTMLUListElement>> = viewChild.required('navMenu');

  ngAfterViewInit(): void {
    this._initResizeObserver();
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
  }

  public handleClick(item: ItemInfo, event: MouseEvent): void {
    handleNavigationClick(item, event, this.router, this.customEventService);
  }

  private _initResizeObserver(): void {
    const container = this.menuContainer().nativeElement;
    const menu = this.navULMenu().nativeElement;

    if (!container) {
      return;
    }

    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === container) {
          this._containerWidth.set(entry.contentRect.width);
        }

        this._updateItemsTotalWidth();
      }
    });

    this._resizeObserver.observe(container);

    if (menu) {
      this._resizeObserver.observe(menu);
    }

    // Initial calculation
    this._containerWidth.set(container.offsetWidth);
    this._updateItemsTotalWidth();
  }

  private _updateItemsTotalWidth(): void {
    const menu = this.navULMenu()?.nativeElement;
    let totalWidth = 0;

    if (menu) {
      const menuItems: NodeListOf<HTMLLIElement> = menu.querySelectorAll('li');
      menuItems.forEach((menuItem) => {
        totalWidth += menuItem.offsetWidth;
      });

      if (totalWidth > 0) {
        this._itemsTotalWidth.set(totalWidth);
      }
    }
  }
}
